import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LogOut, Menu } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { cn } from "@/lib/utils";

export default function DashboardShell({ links, activePath, children }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const goTo = (to) => {
    setMobileOpen(false);
    navigate(to);
  };

  const NavLinks = () => (
    <div className="space-y-1">
      {links.map((l) => (
        <button
          key={l.to}
          data-testid={l.testId}
          onClick={() => goTo(l.to)}
          className={cn(
            "w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors text-left",
            activePath === l.to ? "bg-indigo-50 text-indigo-700" : "text-slate-500 hover:bg-slate-100"
          )}
        >
          <l.icon className="h-4 w-4" />
          {l.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden" data-testid="dashboard-shell">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="md:hidden mr-1 text-slate-600" data-testid="mobile-nav-trigger">
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-6">
                <div className="flex items-center gap-2 mb-8">
                  <BrandLogo className="h-8" />
                </div>
                <NavLinks />
              </SheetContent>
            </Sheet>
            <BrandLogo className="h-8 shrink-0" />
          </div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <span className="text-sm text-slate-500 hidden sm:inline" data-testid="dashboard-user-name">{user?.name}</span>
            <Button variant="ghost" size="icon" data-testid="logout-button" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto flex">
        <nav className="w-56 shrink-0 py-8 pr-6 hidden md:block">
          <NavLinks />
        </nav>
        <main className="flex-1 min-w-0 py-6 sm:py-8 px-4 sm:px-1">{children}</main>
      </div>
    </div>
  );
}
